import { StatusBar, Style } from '@capacitor/status-bar';
import { NavigationBar } from '@hugotomazi/capacitor-navigation-bar';
import { SafeArea } from '@aashu-dubey/capacitor-statusbar-safe-area';
import { Logger } from './Logger';
import { DeviceInfo } from './DeviceInfo';
import { App, AppState } from '@capacitor/app';

export class DeviceStatusBar {

    static inject = () => [DeviceInfo, Logger];
    constructor(private deviceInfo: DeviceInfo, private log: Logger) {}

    private statusBarHeight: number | undefined;
    private navBarHeight: number | undefined;

    init = async (retry: number = 0): Promise<void> => {
        // on ios, the statusbar overlays the app by default, and ionic provides a variable
        if (this.deviceInfo.platform === 'ios') {
            let safeTop = getComputedStyle(document.body).getPropertyValue('--ion-safe-area-top').trim();
            let safeBottom = getComputedStyle(document.body).getPropertyValue('--ion-safe-area-bottom').trim();

            // we invoke this method late in AppContainer, so safari should have set env vars by now,
            // but if not retry, then fall back to reasonable default that should work on all devices
            if (safeTop === '' || safeTop === '0px' || safeBottom === '') {
                if (retry < 3) {
                    this.log.info(`css vars are --ion-safe-area-top: "${safeTop}" --ion-safe-area-bottom: "${safeBottom}", retrying...`);
                    await new Promise(res => setTimeout(res, 100));
                    return await this.init(retry + 1);
                } else {
                    if (safeTop === '' || safeTop === '0px') {
                        this.log.error(`css var --ion-safe-area-top is "${safeTop}", falling back to default`);
                        safeTop = '50px';
                    }
                    if (safeBottom === '') {
                        this.log.error(`css var --ion-safe-area-bottom is "${safeBottom}", falling back to default`);
                        safeBottom = '20px';
                    }
                }
            }

            this.statusBarHeight = parseInt(safeTop);
            this.navBarHeight = parseInt(safeBottom);

            // don't specify the style/color, ios manages it automatically
        }

        // on android we have to manually overlay the status bar, and use a separate plugin to get the height
        else if (this.deviceInfo.platform === 'android') {
            const { top, bottom } = await SafeArea.getSafeAreaInsets();
            this.statusBarHeight = top;
            this.navBarHeight = bottom;

            // fix ionic variables (ionic currently always sets this to '0px' on android)
            document.body.style.setProperty('--ion-safe-area-top', `${this.statusBarHeight}px`);
            document.body.style.setProperty('--ion-safe-area-bottom', `${this.navBarHeight}px`);

            App.addListener('appStateChange', async ({ isActive }: AppState) => {
                // on foreground
                if (isActive){
                    // ensure the status bar has a transparent background
                    // in case the OS has changed that on us, eg b/c the user switched between
                    // light and dark mode and hasn't restarted the app yet
                    setTimeout(async () => {
                        await this.applyTransparentBackgroundToAndroidStatusBar();
                    }, 100);

                    // just in case 100ms was too fast, lets try one more time
                    setTimeout(async () => {
                        await this.applyTransparentBackgroundToAndroidStatusBar();
                    }, 1000);
                }
            });

            this.applyTransparentBackgroundToAndroidStatusBar();
        }

        // on web there is no status bar or nav bar
        else {
            this.statusBarHeight = 0;
            this.navBarHeight = 0;
        }
    };

    private applyTransparentBackgroundToAndroidStatusBar = async () => {
        await StatusBar.setOverlaysWebView({ overlay: true });
        await StatusBar.setStyle({ style: Style.Light });
        await NavigationBar.setTransparency({ isTransparent: true });
        await NavigationBar.setColor({ color: '#ffffff', darkButtons: true });
    };

    // when the status bar state is adjusted while covered by the splash screen, on android 12+
    // it often fails to update itself visually, so this method attempts to nudge it to re-paint
    repaintAndroid = async () => {
        if (this.deviceInfo.platform === 'android') {
            await StatusBar.hide();
            setTimeout(async () => {
                await this.applyTransparentBackgroundToAndroidStatusBar();
                await StatusBar.show();
            }, 1000);
        }
    };

    getHeight = (): number => {
        if (this.statusBarHeight === undefined) {
            throw new Error('Must call init() before getHeight()');
        }
        return this.statusBarHeight!;
    };

    getNavigationBarHeight = (): number => {
        if (this.navBarHeight === undefined) {
            throw new Error('Must call init() before getNavigationBarHeight()');
        }
        return this.navBarHeight!;
    };
};
